/**
 * Mock vendor data for FE design. Shape is the canonical contract BE will
 * implement against. Swap getPropertyVendors / searchVendorCandidates in
 * ./api.ts to hit the real endpoints once they exist.
 */
import type { Vendor, VendorCandidate, VendorTrade } from '@/models/Vendor';

const now = Date.now();

const mkVendor = (
  partial: Omit<Vendor, 'createdAt' | 'updatedAt' | 'propertyId'>,
): Omit<Vendor, 'propertyId'> => ({
  ...partial,
  createdAt: now - 86_400_000,
  updatedAt: now - 86_400_000,
});

export const MOCK_SAVED_VENDORS: Omit<Vendor, 'propertyId'>[] = [
  mkVendor({
    id: 'v-plumber-1',
    trade: 'plumbing',
    source: 'google_places',
    pinned: true,
    name: 'Los Gatos Plumbing & Heating',
    place_id: 'ChIJ_mock_plumber_1',
    phone: '+1 (408) 234-1010',
    website: 'https://losgatos-plumbing.example',
    address: '142 N Santa Cruz Ave, Los Gatos, CA',
    rating: 4.8,
    reviewCount: 312,
    priceLevel: 2,
    distance: { value: 1.4, unit: 'mi' },
  }),
  mkVendor({
    id: 'v-plumber-2',
    trade: 'plumbing',
    source: 'google_places',
    pinned: true,
    name: 'Vasona Drain & Sewer',
    place_id: 'ChIJ_mock_plumber_2',
    phone: '+1 (408) 234-2200',
    address: '88 University Ave, Los Gatos, CA',
    rating: 4.6,
    reviewCount: 187,
    priceLevel: 2,
    distance: { value: 2.1, unit: 'mi' },
  }),
  mkVendor({
    id: 'v-electrician-1',
    trade: 'electrical',
    source: 'google_places',
    pinned: true,
    name: 'Cats Hill Electric',
    place_id: 'ChIJ_mock_elec_1',
    phone: '+1 (408) 410-7755',
    email: 'service@catshill-electric.example',
    website: 'https://catshill-electric.example',
    address: '23 E Main St, Los Gatos, CA',
    rating: 4.9,
    reviewCount: 421,
    priceLevel: 3,
    distance: { value: 0.9, unit: 'mi' },
  }),
  mkVendor({
    id: 'v-hvac-1',
    trade: 'air_conditioning_and_heat',
    source: 'google_places',
    pinned: true,
    name: 'Sierra Azul Heating & Air',
    place_id: 'ChIJ_mock_hvac_1',
    phone: '+1 (408) 612-8090',
    address: '450 Blossom Hill Rd, Los Gatos, CA',
    rating: 4.7,
    reviewCount: 256,
    priceLevel: 3,
    distance: { value: 3.4, unit: 'mi' },
  }),
  mkVendor({
    id: 'v-hvac-manual-1',
    trade: 'air_conditioning_and_heat',
    source: 'manual',
    pinned: true,
    name: "Mike's HVAC (referral from neighbor)",
    phone: '+1 (408) 999-0123',
    email: 'mike@mikeshvac.example',
    notes: 'Marcia next door uses him. Cash discount.',
    rating: 5,
    reviewCount: 0,
  }),
  // Spread across the broader trade list so the directory feels lived-in.
  mkVendor({
    id: 'v-roof-1',
    trade: 'roofing',
    source: 'google_places',
    pinned: true,
    name: 'Skyline Roofing & Solar',
    place_id: 'ChIJ_mock_roof_1',
    phone: '+1 (408) 555-1818',
    website: 'https://skyline-roofing.example',
    address: '912 Winchester Blvd, Los Gatos, CA',
    rating: 4.7,
    reviewCount: 264,
    priceLevel: 3,
    distance: { value: 2.3, unit: 'mi' },
  }),
  mkVendor({
    id: 'v-landscape-1',
    trade: 'landscaping',
    source: 'google_places',
    pinned: true,
    name: 'Green Thumb Landscapes',
    place_id: 'ChIJ_mock_land_1',
    phone: '+1 (408) 555-0182',
    address: '54 Loma Alta Ave, Los Gatos, CA',
    rating: 4.7,
    reviewCount: 198,
    priceLevel: 2,
    distance: { value: 1.1, unit: 'mi' },
  }),
  mkVendor({
    id: 'v-paint-1',
    trade: 'painting',
    source: 'google_places',
    pinned: true,
    name: 'Saratoga Painting Co.',
    place_id: 'ChIJ_mock_paint_1',
    phone: '+1 (408) 555-2727',
    website: 'https://saratoga-painting.example',
    address: '200 Saratoga-Los Gatos Rd, Los Gatos, CA',
    rating: 4.8,
    reviewCount: 142,
    priceLevel: 3,
    distance: { value: 1.9, unit: 'mi' },
  }),
  mkVendor({
    id: 'v-handyman-1',
    trade: 'handyman',
    source: 'manual',
    pinned: true,
    name: "Tony's Handyman Service",
    phone: '+1 (408) 555-6464',
    notes: 'Met him at the Almond Festival. Honest, shows up on time.',
    rating: 5,
    reviewCount: 0,
  }),
  mkVendor({
    id: 'v-pest-1',
    trade: 'pest_control',
    source: 'google_places',
    pinned: true,
    name: 'Sentinel Pest Control',
    place_id: 'ChIJ_mock_pest_1',
    phone: '+1 (408) 555-3434',
    website: 'https://sentinel-pest.example',
    address: '1450 S Bascom Ave, Campbell, CA',
    rating: 4.5,
    reviewCount: 312,
    priceLevel: 2,
    distance: { value: 4.2, unit: 'mi' },
  }),
  mkVendor({
    id: 'v-inspect-1',
    trade: 'home_inspection',
    source: 'google_places',
    pinned: true,
    name: 'Bay Area Home Inspectors',
    place_id: 'ChIJ_mock_inspect_1',
    phone: '+1 (408) 555-2010',
    website: 'https://bayareahomeinspectors.example',
    rating: 4.9,
    reviewCount: 521,
    priceLevel: 3,
    distance: { value: 3.6, unit: 'mi' },
  }),
  mkVendor({
    id: 'v-realestate-1',
    trade: 'real_estate',
    source: 'google_places',
    pinned: true,
    name: 'Avenida — Los Gatos',
    place_id: 'ChIJ_mock_realestate_1',
    phone: '+1 (408) 555-7000',
    website: 'https://avenida.example/los-gatos',
    address: '15 N Santa Cruz Ave, Los Gatos, CA',
    rating: 4.9,
    reviewCount: 142,
    priceLevel: 3,
    distance: { value: 0.6, unit: 'mi' },
  }),
  mkVendor({
    id: 'v-mortgage-1',
    trade: 'mortgage',
    source: 'manual',
    pinned: true,
    name: 'Anita Park — Bay Area Mortgage',
    phone: '+1 (408) 555-9595',
    email: 'anita@bayareamortgage.example',
    notes: 'Helped us with the original loan. Fast preapprovals.',
    rating: 5,
    reviewCount: 0,
  }),
  // Tradehaus (our own brand) across the build/install trades it covers.
  mkVendor({
    id: 'v-tradehaus-gc',
    trade: 'general_contracting',
    source: 'google_places',
    pinned: true,
    name: 'Tradehaus',
    place_id: 'ChIJ_tradehaus_gc',
    phone: '+1 (408) 430-3794',
    website: 'https://tradehaus.example',
    address: '500 N Santa Cruz Ave, Los Gatos, CA',
    rating: 4.9,
    reviewCount: 234,
    priceLevel: 3,
    distance: { value: 0.9, unit: 'mi' },
  }),
  mkVendor({
    id: 'v-tradehaus-smart',
    trade: 'smart_home',
    source: 'google_places',
    pinned: true,
    name: 'Tradehaus',
    place_id: 'ChIJ_tradehaus_smart',
    phone: '+1 (408) 430-3794',
    website: 'https://tradehaus.example',
    address: '500 N Santa Cruz Ave, Los Gatos, CA',
    rating: 4.9,
    reviewCount: 234,
    priceLevel: 3,
    distance: { value: 0.9, unit: 'mi' },
  }),
  mkVendor({
    id: 'v-tradehaus-flooring',
    trade: 'flooring',
    source: 'google_places',
    pinned: true,
    name: 'Tradehaus',
    place_id: 'ChIJ_tradehaus_flooring',
    phone: '+1 (408) 430-3794',
    website: 'https://tradehaus.example',
    address: '500 N Santa Cruz Ave, Los Gatos, CA',
    rating: 4.9,
    reviewCount: 234,
    priceLevel: 3,
    distance: { value: 0.9, unit: 'mi' },
  }),
];

/**
 * Mocked candidate pools — only the trades we've populated for the demo.
 * Other trades resolve to [] via the `?? []` lookup in api.ts. Partial
 * so we don't need an empty entry for each of the 28 trades.
 */
export const MOCK_CANDIDATES_BY_TRADE: Partial<Record<VendorTrade, VendorCandidate[]>> = {
  plumbing: [
    {
      trade: 'plumbing',
      source: 'google_places',
      name: 'Almaden Plumbing Co.',
      place_id: 'ChIJ_mock_cand_plumber_1',
      address: '14 N Santa Cruz Ave, Los Gatos, CA',
      phone: '+1 (408) 555-1010',
      website: 'https://almaden-plumbing.example',
      rating: 4.7,
      reviewCount: 540,
      priceLevel: 2,
      distance: { value: 0.8, unit: 'mi' },
      location: { lat: 37.225, lng: -121.975 },
      photoUrl: 'https://picsum.photos/seed/almaden-plumbing/480/240',
      category: 'Plumber',
      hoursLabel: 'Open · Closes 6 PM',
    },
    {
      trade: 'plumbing',
      source: 'google_places',
      name: 'Old Town Plumbing 24/7',
      place_id: 'ChIJ_mock_cand_plumber_2',
      address: '900 Pollard Rd, Los Gatos, CA',
      phone: '+1 (408) 555-2424',
      website: 'https://oldtown-plumbing.example',
      rating: 4.5,
      reviewCount: 220,
      priceLevel: 3,
      distance: { value: 1.6, unit: 'mi' },
      location: { lat: 37.245, lng: -121.952 },
      photoUrl: 'https://picsum.photos/seed/pipeworks/480/240',
      category: 'Plumber · Emergency service',
      hoursLabel: 'Open 24 hours',
    },
    {
      trade: 'plumbing',
      source: 'google_places',
      name: 'Saratoga Creek Sewer & Drain',
      place_id: 'ChIJ_mock_cand_plumber_3',
      address: '300 University Ave, Los Gatos, CA',
      phone: '+1 (408) 555-3030',
      rating: 4.3,
      reviewCount: 89,
      priceLevel: 1,
      distance: { value: 2.4, unit: 'mi' },
      location: { lat: 37.215, lng: -121.99 },
      photoUrl: 'https://picsum.photos/seed/sewer-pros/480/240',
      category: 'Plumber',
      hoursLabel: 'Closed · Opens 7 AM Mon',
    },
    {
      trade: 'plumbing',
      source: 'google_places',
      name: 'Cats Hill Plumbing',
      place_id: 'ChIJ_mock_cand_plumber_4',
      address: '215 Bachman Ave, Los Gatos, CA',
      phone: '+1 (408) 555-4242',
      website: 'https://catshill-plumbing.example',
      rating: 4.6,
      reviewCount: 312,
      priceLevel: 2,
      distance: { value: 1.2, unit: 'mi' },
      location: { lat: 37.232, lng: -121.972 },
      photoUrl: 'https://picsum.photos/seed/catshill-plumbing/480/240',
      category: 'Plumber',
      hoursLabel: 'Open · Closes 6 PM',
    },
    {
      trade: 'plumbing',
      source: 'google_places',
      name: 'Vasona Pipe & Drain',
      place_id: 'ChIJ_mock_cand_plumber_5',
      address: '410 Blossom Hill Rd, Los Gatos, CA',
      phone: '+1 (408) 555-5151',
      rating: 4.4,
      reviewCount: 156,
      priceLevel: 2,
      distance: { value: 1.8, unit: 'mi' },
      location: { lat: 37.241, lng: -121.96 },
      photoUrl: 'https://picsum.photos/seed/vasona-pipe/480/240',
      category: 'Plumber · Water heater repair',
      hoursLabel: 'Open · Closes 7 PM',
    },
    {
      trade: 'plumbing',
      source: 'google_places',
      name: 'Lexington Plumbing Services',
      place_id: 'ChIJ_mock_cand_plumber_6',
      address: '88 Wedgewood Ave, Los Gatos, CA',
      phone: '+1 (408) 555-6262',
      website: 'https://lexington-plumbing.example',
      rating: 4.8,
      reviewCount: 421,
      priceLevel: 3,
      distance: { value: 2.7, unit: 'mi' },
      location: { lat: 37.208, lng: -121.985 },
      photoUrl: 'https://picsum.photos/seed/lexington-plumbing/480/240',
      category: 'Plumber',
      hoursLabel: 'Open · Closes 8 PM',
    },
    {
      trade: 'plumbing',
      source: 'google_places',
      name: 'Highway 17 Plumbing',
      place_id: 'ChIJ_mock_cand_plumber_7',
      address: '1200 Los Gatos-Saratoga Rd, Los Gatos, CA',
      phone: '+1 (408) 555-1717',
      rating: 4.2,
      reviewCount: 78,
      priceLevel: 1,
      distance: { value: 3.1, unit: 'mi' },
      location: { lat: 37.252, lng: -121.99 },
      photoUrl: 'https://picsum.photos/seed/hwy17-plumbing/480/240',
      category: 'Plumber',
      hoursLabel: 'Closed · Opens 8 AM Mon',
    },
    {
      trade: 'plumbing',
      source: 'google_places',
      name: 'Blossom Hill Plumbing',
      place_id: 'ChIJ_mock_cand_plumber_8',
      address: '755 Blossom Hill Rd, Los Gatos, CA',
      phone: '+1 (408) 555-7878',
      rating: 4.5,
      reviewCount: 287,
      priceLevel: 2,
      distance: { value: 1.5, unit: 'mi' },
      location: { lat: 37.219, lng: -121.965 },
      photoUrl: 'https://picsum.photos/seed/blossomhill-plumbing/480/240',
      category: 'Plumber · Emergency service',
      hoursLabel: 'Open 24 hours',
    },
    {
      trade: 'plumbing',
      source: 'google_places',
      name: 'Monte Sereno Plumbing',
      place_id: 'ChIJ_mock_cand_plumber_9',
      address: '50 Daves Ave, Monte Sereno, CA',
      phone: '+1 (408) 555-8989',
      website: 'https://montesereno-plumbing.example',
      rating: 4.7,
      reviewCount: 198,
      priceLevel: 3,
      distance: { value: 2.0, unit: 'mi' },
      location: { lat: 37.236, lng: -121.992 },
      photoUrl: 'https://picsum.photos/seed/montesereno-plumbing/480/240',
      category: 'Plumber',
      hoursLabel: 'Open · Closes 6 PM',
    },
    {
      trade: 'plumbing',
      source: 'google_places',
      name: 'Bay Area Plumbing Pros',
      place_id: 'ChIJ_mock_cand_plumber_10',
      address: '900 Winchester Blvd, Los Gatos, CA',
      phone: '+1 (408) 555-9090',
      rating: 4.0,
      reviewCount: 67,
      priceLevel: 1,
      distance: { value: 2.8, unit: 'mi' },
      location: { lat: 37.258, lng: -121.952 },
      photoUrl: 'https://picsum.photos/seed/bayarea-plumbing/480/240',
      category: 'Plumber',
      hoursLabel: 'Open · Closes 5 PM',
    },
  ],
  electrical: [
    {
      trade: 'electrical',
      source: 'google_places',
      name: 'South Bay Electric',
      place_id: 'ChIJ_mock_cand_elec_1',
      address: '21 E Main St, Los Gatos, CA',
      phone: '+1 (408) 555-7755',
      website: 'https://southbay-electric.example',
      rating: 4.8,
      reviewCount: 612,
      priceLevel: 2,
      distance: { value: 1.1, unit: 'mi' },
      location: { lat: 37.228, lng: -121.968 },
      photoUrl: 'https://picsum.photos/seed/voltage-bros/480/240',
      category: 'Electrician',
      hoursLabel: 'Open · Closes 7 PM',
    },
    {
      trade: 'electrical',
      source: 'google_places',
      name: 'El Gato Electric',
      place_id: 'ChIJ_mock_cand_elec_2',
      address: '12 Saratoga Ave, Los Gatos, CA',
      phone: '+1 (408) 555-4040',
      rating: 4.4,
      reviewCount: 134,
      priceLevel: 3,
      distance: { value: 2.0, unit: 'mi' },
      location: { lat: 37.222, lng: -121.985 },
      photoUrl: 'https://picsum.photos/seed/circuit-saviors/480/240',
      category: 'Electrician',
      hoursLabel: 'Open · Closes 5 PM',
    },
  ],
  air_conditioning_and_heat: [
    {
      trade: 'air_conditioning_and_heat',
      source: 'google_places',
      name: 'Loma Prieta Heating & Air',
      place_id: 'ChIJ_mock_cand_hvac_1',
      address: '450 Winchester Blvd, Los Gatos, CA',
      phone: '+1 (408) 555-8090',
      website: 'https://lomaprieta-air.example',
      rating: 4.6,
      reviewCount: 298,
      priceLevel: 3,
      distance: { value: 1.7, unit: 'mi' },
      location: { lat: 37.252, lng: -121.965 },
      photoUrl: 'https://picsum.photos/seed/coolbreeze/480/240',
      category: 'HVAC contractor',
      hoursLabel: 'Open · Closes 8 PM',
    },
  ],
  roofing: [],
  landscaping: [],
  general_contracting: [],
};
